import Ajv, { type ValidateFunction } from 'ajv'
import type { ActionContext, ActionDefinition, ActionId, ActionOutcome, ActionRisk, ActionSurface } from './action'
import * as telemetry from './telemetry'

const ajv = new Ajv({ allErrors: false, coerceTypes: false, useDefaults: true })
const registry = new Map<ActionId, ActionDefinition>()
const validators = new Map<ActionId, ValidateFunction>()

export const registerAction = <Args, Result>(definition: ActionDefinition<Args, Result>): void => {
  if (registry.has(definition.id)) throw new Error(`Action ${definition.id} already registered`)
  validators.set(definition.id, ajv.compile(definition.argsSchema))
  registry.set(definition.id, definition as ActionDefinition)
}

export const getAction = (id: ActionId): ActionDefinition | undefined => registry.get(id)

export const listActions = (filter?: { surface?: ActionSurface; domain?: string; risk?: ActionRisk | ActionRisk[]; hasTour?: boolean }): ActionDefinition[] =>
  Array.from(registry.values()).filter((action) => {
    if (filter?.surface && !action.surfaces.includes(filter.surface)) return false
    if (filter?.domain && action.domain !== filter.domain) return false
    if (filter?.risk) {
      const risks = Array.isArray(filter.risk) ? filter.risk : [filter.risk]
      if (!risks.includes(action.risk)) return false
    }
    if (filter?.hasTour === true && !action.tourId) return false
    if (filter?.hasTour === false && action.tourId) return false
    return true
  })

export const invokeAction = async <Args, Result>(id: ActionId, args: Args, context: ActionContext): Promise<ActionOutcome<Result>> => {
  const correlationId = context.correlationId ?? crypto.randomUUID()
  const action = registry.get(id) as ActionDefinition<Args, Result> | undefined
  if (!action) return { kind: 'FAILED', message: `Unknown action ${id}`, recoverable: false, correlationId }
  if (!action.surfaces.includes(context.invokedFrom)) {
    return { kind: 'FAILED', message: `Action ${id} not permitted from ${context.invokedFrom}`, recoverable: false, correlationId }
  }
  const validate = validators.get(id)
  if (!validate || !validate(args)) return { kind: 'FAILED', message: ajv.errorsText(validate?.errors), recoverable: false, correlationId }

  const invocationContext = { ...context, correlationId }
  if (action.permission && !(await action.permission(invocationContext))) {
    return { kind: 'BLOCKED_BY_PERMISSION', message: `You don't have permission to ${action.label.toLowerCase()}.`, missingPermission: id, correlationId }
  }
  if (action.quota) {
    const quota = await action.quota(invocationContext)
    if (!quota.ok) return { kind: 'BLOCKED_BY_QUOTA', message: quota.reason ?? 'Quota exceeded', quotaKind: id, correlationId }
  }

  const outcome = await action.run(args, invocationContext)
  const outcomeWithCorrelationId = { ...outcome, correlationId } as ActionOutcome<Result>
  telemetry.record({ actionId: id, outcome: outcome.kind, invokedFrom: context.invokedFrom, correlationId })
  return outcomeWithCorrelationId
}

/** Returns a tour reference; the caller launches it through useTourGuide. */
export const teachAction = async (id: ActionId, context: ActionContext): Promise<ActionOutcome<{ tourId: string }>> => {
  const correlationId = context.correlationId ?? crypto.randomUUID()
  const action = registry.get(id)
  if (!action) return { kind: 'FAILED', message: `Unknown action ${id}`, recoverable: false, correlationId }
  if (!action.tourId) return { kind: 'FAILED', message: `Action ${id} has no attached tour`, recoverable: false, correlationId }
  telemetry.record({ actionId: id, outcome: 'TAUGHT', invokedFrom: context.invokedFrom, correlationId })
  return { kind: 'COMPLETED', result: { tourId: action.tourId }, correlationId }
}

// Test hook only. Consumers must not clear registered actions at runtime.
export const __resetActionRegistryForTests = (): void => { registry.clear(); validators.clear() }

declare global {
  interface Window {
    __actionRegistry?: {
      listActions: typeof listActions
      getAction: typeof getAction
      invokeAction: typeof invokeAction
      teachAction: typeof teachAction
    }
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__actionRegistry = {
    listActions,
    getAction,
    invokeAction,
    teachAction,
  }
}
